import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar } from "@mui/material";
import dayjs from "dayjs";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseTooltip from "../../components/ui/BaseTooltip";
import { fetchCustomers } from "../../services/api/admin";
import { debounce, formatPhoneNumber } from "../../utils/helpers";
import { joinNonEmpty } from "../../utils/displayValue";

const CustomerEntitiesTable = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  const queryParams = {
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    search,
    parentClientId: id,
  };

  const { data, isPending } = useQuery({
    queryKey: ["adminCustomers", queryParams],
    queryFn: () => fetchCustomers(queryParams),
    enabled: !!id,
    placeholderData: (prev) => prev,
  });

  const entities = data?.customers || [];
  const rowCount = data?.totalCount || 0;

  const debouncedSearchRef = useRef(
    debounce((value) => {
      setSearch(value.trim());
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500),
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedSearchRef.current(e.target.value);
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.6,
      minWidth: 90,
      renderCell: (params) => <p className="text-primary cursor-pointer">{params?.value}</p>,
    },
    {
      field: "clientName",
      headerName: "Name",
      flex: 1.6,
      minWidth: 190,
      renderCell: (params) => (
        <div className="flex items-center gap-2 cursor-pointer">
          <Avatar sx={{ width: 28, height: 28, fontSize: 12 }} src={params.row?.clientLogo?.url}>
            {params.row.clientName?.[0]}
          </Avatar>
          <p className="text-primary capitalize">{params.row.clientName}</p>
        </div>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.8,
      minWidth: 200,
      renderCell: (params) => {
        const address = params.row.address;
        const fullAddress = joinNonEmpty([
          address?.addressLine1,
          address?.city,
          address?.state,
          address?.zipCode,
          address?.country,
        ]);

        if (!fullAddress) return <p>-</p>;

        return (
          <BaseTooltip placement="top" content={<p className="text-white">{fullAddress}</p>}>
            <p className="truncate overflow-hidden whitespace-nowrap">{fullAddress}</p>
          </BaseTooltip>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.6,
      minWidth: 190,
      renderCell: (params) => <p>{params?.value || "-"}</p>,
    },
    {
      field: "phoneNumber",
      headerName: "Mobile",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => <p>{formatPhoneNumber(params?.value) || "-"}</p>,
    },
    {
      field: "managerName",
      headerName: "Contact Person",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => <p className="capitalize">{params?.value || "-"}</p>,
    },
    {
      field: "branch",
      headerName: "Tech Domain Branch",
      flex: 1.3,
      minWidth: 160,
      renderCell: (params) => <p>{params.row.branch?.name || "-"}</p>,
    },
    {
      field: "team",
      headerName: "Service Delivery Team",
      flex: 1.3,
      minWidth: 170,
      renderCell: (params) => <p>{params.row.team?.name || "-"}</p>,
    },
    {
      field: "secondaryTeam",
      headerName: "Operations Team",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => <p>{params.row.secondaryTeam?.name || "-"}</p>,
    },
    {
      field: "totalWorkOrders",
      headerName: "Work Orders",
      flex: 0.9,
      minWidth: 120,
      renderCell: (params) => <p className="text-primary">{params?.value ?? 0}</p>,
    },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => <p>{dayjs(params.value).format("DD-MM-YYYY")}</p>,
    },
  ];

  // The entity opens as a read-only form; nothing here is editable from admin.
  const handleCellClick = (params) => {
    if (params.field === "id" || params.field === "clientName") {
      navigate(`/admin/customers/entity/${params.row.id}`);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg px-5 py-3.5 shadow-md sticky top-10 z-10">
        <div className="flex justify-end w-full">
          <div className="w-full sm:w-[28%]">
            <BaseTextField
              id="customerEntitiesSearch"
              name="customerEntitiesSearch"
              type="search"
              autoComplete="off"
              placeholder="Search by name, email or ID"
              value={searchQuery}
              onChange={handleSearchChange}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>
        </div>
      </div>

      <BaseDataTableV2
        className="max-h-[calc(100vh-320px)]"
        rowHeight={38}
        columns={columns}
        data={entities}
        isLoading={isPending}
        onCellClick={handleCellClick}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowCount={rowCount}
        pageSizeOptions={[10, 20, 50, 100]}
        hideFooter={false}
      />
    </>
  );
};

export default CustomerEntitiesTable;
